import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import './StaticPage.css';

const LAST_UPDATED = 'March 1, 2025';
const SITE_NAME = 'AstroEpanchang';
const SITE_URL = 'https://www.astroepanchang.com';

const sections = [
  {
    title: '1. Who We Are',
    content: `${SITE_NAME} (${SITE_URL}) is an online Vedic astrology platform providing free horoscope generation, daily panchangam, zodiac information, and related astrology services. We are committed to protecting your personal data and respecting your privacy rights under the General Data Protection Regulation (GDPR) and applicable data protection laws.`,
  },
  {
    title: '2. Data We Collect',
    items: [
      'Birth details you enter (date, time, place of birth) for horoscope or panchang calculations — processed in real time and not stored unless you explicitly save them.',
      'Contact form submissions: name, email address, and message content.',
      'Usage data collected automatically via analytics cookies: pages visited, time on site, browser type, and approximate location (country/city level).',
      'Advertising data collected by Google AdSense to serve relevant ads.',
      'Newsletter subscriptions: email address only.',
    ],
  },
  {
    title: '3. Legal Basis for Processing',
    items: [
      'Consent – for analytics and advertising cookies, and newsletter subscriptions.',
      'Legitimate interest – for essential site functionality and fraud prevention.',
      'Contract performance – when you use our paid services or consultation features.',
      'Legal obligation – where required by applicable law.',
    ],
  },
  {
    title: '4. How We Use Your Data',
    items: [
      'To calculate and display horoscope, panchang, and astrology results.',
      'To respond to contact form enquiries.',
      'To send newsletters (only if you subscribed; unsubscribe any time).',
      'To improve site performance via aggregated analytics.',
      'To serve personalised advertisements via Google AdSense.',
    ],
  },
  {
    title: '5. Cookies',
    content: `We use the following categories of cookies:`,
    items: [
      'Essential cookies – necessary for the site to function (session management, security). Always active.',
      'Analytics cookies – Google Analytics to understand site usage. Requires consent.',
      'Advertising cookies – Google AdSense to serve relevant ads. Requires consent.',
    ],
    extra: 'You can manage or withdraw cookie consent at any time using the cookie banner at the bottom of the page.',
  },
  {
    title: '6. Data Sharing & Third Parties',
    items: [
      'Google Analytics (analytics & measurement) – governed by Google Privacy Policy.',
      'Google AdSense (advertising) – governed by Google Privacy Policy.',
      'Hosting & infrastructure providers under strict data processing agreements.',
      'We do not sell, rent, or trade your personal data with any third parties for marketing purposes.',
    ],
  },
  {
    title: '7. Data Retention',
    content: 'Birth details entered for real-time calculations are not stored on our servers after the calculation is returned. Contact form data is retained for up to 12 months. Newsletter subscriptions are retained until you unsubscribe. Analytics data is retained for 26 months (Google Analytics default).',
  },
  {
    title: '8. Your Rights Under GDPR',
    items: [
      'Right of access – request a copy of the personal data we hold about you.',
      'Right to rectification – request correction of inaccurate data.',
      'Right to erasure ("right to be forgotten") – request deletion of your data.',
      'Right to restrict processing – ask us to pause processing of your data.',
      'Right to data portability – receive your data in a structured, machine-readable format.',
      'Right to object – object to processing based on legitimate interest or direct marketing.',
      'Right to withdraw consent – withdraw at any time without affecting prior processing.',
      'Right to lodge a complaint – with your national data protection authority.',
    ],
    extra: 'To exercise any of these rights, use the contact form on our Contact page.',
  },
  {
    title: '9. International Transfers',
    content: 'Some of our third-party service providers (such as Google) may process data outside the European Economic Area (EEA). Where this occurs, appropriate safeguards are in place, such as Standard Contractual Clauses approved by the European Commission.',
  },
  {
    title: '10. Children\'s Privacy',
    content: `${SITE_NAME} is not directed at children under the age of 16. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us immediately.`,
  },
  {
    title: '11. Changes to This Policy',
    content: 'We may update this Privacy Policy from time to time. The "Last Updated" date at the top of this page indicates when the latest revision was made. Continued use of the site after changes constitutes acceptance of the updated policy.',
  },
];

const Privacy = () => {
  const hydrated = useHydrated();
  return (
    <div className="static-page">
      <SEO
        title="Privacy Policy - GDPR Compliant"
        description={`${SITE_NAME}'s Privacy Policy. Learn how we collect, use, and protect your personal data in accordance with GDPR and applicable data protection laws.`}
        keywords="privacy policy, GDPR, data protection, cookies, personal data"
        canonicalPath="/privacy"
        noindex={false}
      />
      <div className="container">
        <motion.div
          className="static-page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>Privacy <span className="gradient-text">Policy</span></h1>
          <p className="static-meta">Last updated: {LAST_UPDATED}</p>
          <p className="static-intro">
            This Privacy Policy explains how {SITE_NAME} collects, uses, and protects your personal
            data in accordance with the EU General Data Protection Regulation (GDPR) and other
            applicable data protection laws.
          </p>
        </motion.div>

        <motion.div
          className="static-content"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {sections.map((sec, i) => (
            <div key={i} className="static-section">
              <h2>{sec.title}</h2>
              {sec.content && <p>{sec.content}</p>}
              {sec.items && (
                <ul>
                  {sec.items.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              )}
              {sec.extra && <p className="static-note">{sec.extra}</p>}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
